import React, { forwardRef, useCallback, useEffect, useMemo, useState } from "react";

import { Box, Button, CircularProgress, LinearProgress, Typography } from "@mui/material";
import { AICard } from "components/Report-generator/components/cards/AICard";
import { BenchmarkComparisonCard } from "components/Report-generator/components/cards/BenchmarkComparisonCard";
import { CashFlowTimelineCard } from "components/Report-generator/components/cards/CashFlowTimelineCard";
import { ChartCard } from "components/Report-generator/components/cards/ChartCard";
import { DataFreshnessCard } from "components/Report-generator/components/cards/DataFreshnessCard";
import { DataTableCard } from "components/Report-generator/components/cards/DataTableCard";
import { ExposureBreakdownCard } from "components/Report-generator/components/cards/ExposureBreakdownCard";
import { KpiCard } from "components/Report-generator/components/cards/KpiCard";
import { MacroBriefCard } from "components/Report-generator/components/cards/MacroBriefCard";
import { PerformanceAttributionCard } from "components/Report-generator/components/cards/PerformanceAttributionCard";
import { SummaryCard } from "components/Report-generator/components/cards/SummaryCard";
import { TextCard } from "components/Report-generator/components/cards/TextCard";
import { UnfundedCommitmentsCard } from "components/Report-generator/components/cards/UnfundedCommitmentsCard";
import { CardTimeRangeChip } from "components/Report-generator/components/CardTimeRangeChip";
import { ReportHeader } from "components/Report-generator/components/Canvas/ReportHeader";
import { SectionBreakHeader } from "components/Report-generator/components/Canvas/SectionBreakHeader";
import { TitlePageContent } from "components/Report-generator/components/Canvas/TitlePageContent";
import { CardSizeProvider } from "components/Report-generator/context/CardSizeContext";
import { TableExpandProvider } from "components/Report-generator/context/TableExpandContext";
import { computeDataFreshnessGridH, computeTableGridH, previewCardWidth } from "components/Report-generator/utils/gridUtils";
import { getPageMetrics } from "components/Report-generator/utils/pageMetrics";
import { getPageTitleValues } from "components/Report-generator/utils/pageTitles";
import { assignRowPaths, splitPagesForOverflow } from "components/Report-generator/utils/splitTablePages";
import { useMeasuredTableHeights } from "components/Report-generator/hooks/use-measured-table-heights";
import type {
  BenchmarkComparisonCardData,
  CanvasCard,
  CashFlowTimelineCardData,
  Chart,
  Currency,
  DataFreshnessCardData,
  DataTableCardData,
  DateRangeValue,
  ExposureCardData,
  Filters,
  KPICardData,
  MacroBriefCardConfig,
  MacroBriefCardData,
  PerformanceAttributionCardData,
  ReportMeta,
  ReportOrientation,
  ReportPage,
  SummaryCardData,
  TableCardConfig,
  TableRow,
  UnfundedCardData
} from "store/types";
import { GRID_CONSTANTS } from "store/types/reports/report-generator";

const EMPTY_SET: Set<string> = new Set();
const noop = () => {};

interface Props {
  cards: CanvasCard[];
  pages: ReportPage[];
  reportMeta: ReportMeta;
  period: DateRangeValue | null;
  currency: Currency;
  orientation: ReportOrientation;
  filters?: Filters;
  expandedRows: Record<string, Set<string>>;
  onToggleExpand: (cardId: string, rowKey: string) => void;
  onBack: () => void;
  onExportPDF: () => Promise<void>;
  autoExport?: boolean;
}

const PreviewMode = forwardRef<HTMLDivElement, Props>(
  (
    {
      cards,
      pages,
      reportMeta,
      period,
      currency,
      orientation,
      filters,
      expandedRows,
      onToggleExpand,
      onBack,
      onExportPDF,
      autoExport
    },
    ref
  ) => {
    const { width: pageWidth, height: pageHeight } = getPageMetrics(orientation);
    const [exporting, setExporting] = useState(false);

    const handleExport = useCallback(async () => {
      setExporting(true);
      try {
        await onExportPDF();
      } finally {
        setExporting(false);
      }
    }, [onExportPDF]);

    useEffect(() => {
      if (!autoExport) return undefined;
      const timer = window.setTimeout(() => {
        void handleExport();
      }, 500);
      return () => window.clearTimeout(timer);
    }, [autoExport, handleExport]);

    // Tag every table row with a stable __path so its DOM-measured height can be
    // looked up during pagination, independent of which page chunk it lands on.
    const cardsWithPaths = useMemo(
      () =>
        cards.map((card) => {
          if (card.config.type !== "table" || !card.data) return card;
          const data = card.data as DataTableCardData;
          return { ...card, data: { ...data, data: assignRowPaths(data.data) } };
        }),
      [cards]
    );
    const tableCards = useMemo(
      () => cardsWithPaths.filter((c) => c.config.type === "table" && !!c.data),
      [cardsWithPaths]
    );
    const { measured, measureNode } = useMeasuredTableHeights(tableCards, pageWidth, previewCardWidth, orientation);

    const { groupedPages, resolvedPageMeta, pageCount, cardOffsets, originalCardIds } = useMemo(() => {
      // Exact DOM-measured heights when available; undefined falls back to the estimate.
      const getRowHeight = (cardId: string, row: TableRow) => measured?.get(cardId)?.rows.get(row.__path ?? "");
      const getHeaderHeight = (cardId: string) => measured?.get(cardId)?.header;

      // Pre-process: compute content-driven gridH for table cards
      const adjustedCards = cardsWithPaths.map((card) => {
        // Data Freshness cards size to one grid row per data entry.
        if (card.config.type === "dataFreshness" && card.data) {
          const gridH = computeDataFreshnessGridH(card);
          return gridH === card.gridH ? card : { ...card, gridH };
        }
        if (card.config.type !== "table" || !card.data) return card;
        const expanded = expandedRows[card.id] ?? EMPTY_SET;
        const contentGridH = computeTableGridH(
          card,
          expanded,
          previewCardWidth(pageWidth, card.gridW),
          undefined,
          undefined,
          undefined,
          false
        );
        if (contentGridH === card.gridH) return card;
        return { ...card, gridH: contentGridH };
      });

      const rawGroupedPages: CanvasCard[][] = Array.from({ length: pages.length }, (_, i) =>
        adjustedCards.filter((c) => c.page === i)
      );
      const result = splitPagesForOverflow(rawGroupedPages, pages, pageHeight, pageWidth, expandedRows, {
        contentPadding: 96,
        isEditMode: false,
        getRowHeight,
        getHeaderHeight
      });

      // Post-process: recompute gridH for each table segment based on its actual
      // (sliced) data so continuation cards don't inherit the full-table height.
      const sizedPages = result.pages.map((pageCards) =>
        pageCards.map((card) => {
          if (card.config.type !== "table" || !card.data) return card;
          const origId = result.originalCardIds[card.id] ?? card.id;
          const expanded = expandedRows[origId] ?? EMPTY_SET;
          const offsets = result.cardOffsets[card.id];
          const segmentGridH = computeTableGridH(
            card,
            expanded,
            previewCardWidth(pageWidth, card.gridW),
            1,
            offsets,
            undefined,
            false
          );
          if (segmentGridH === card.gridH) return card;
          return { ...card, gridH: segmentGridH };
        })
      );

      return {
        groupedPages: sizedPages,
        resolvedPageMeta: result.pageMeta,
        pageCount: sizedPages.length,
        cardOffsets: result.cardOffsets,
        originalCardIds: result.originalCardIds
      };
    }, [cardsWithPaths, pages, pageHeight, pageWidth, expandedRows, measured]);

    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          minHeight: "calc(100vh - 120px)",
          bgcolor: "#F5F7FA",
          overflowX: "auto"
        }}
      >
        {/* Off-screen tables measured for pixel-perfect pagination. */}
        {measureNode}
        {!autoExport && (
          <Box
            sx={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              height: 60,
              bgcolor: "#0F5D5E",
              display: "flex",
              alignItems: "center",
              p: "0 24px 0 80px",
              gap: 2,
              zIndex: 1500,
              boxShadow: "0 2px 8px rgba(0,0,0,0.2)"
            }}
          >
            <Button
              onClick={onBack}
              sx={{
                border: "1px solid rgba(255,255,255,0.28)",
                borderRadius: "999px",
                p: "7px 14px",
                color: "#FFFFFF",
                fontSize: 13,
                fontWeight: 600,
                textTransform: "none",
                "&:hover": { bgcolor: "rgba(255,255,255,0.1)" }
              }}
            >
              Back to editor
            </Button>
            <Typography
              sx={{ flex: 1, textAlign: "center", color: "rgba(255,255,255,0.78)", fontSize: 12, fontWeight: 600 }}
            >
              Preview Mode | {reportMeta.title} ({pageCount} page{pageCount !== 1 ? "s" : ""})
            </Typography>
            <Button
              onClick={handleExport}
              disabled={exporting}
              sx={{
                borderRadius: "999px",
                p: "8px 16px",
                bgcolor: exporting ? "rgba(255,255,255,0.15)" : "#FFFFFF",
                color: exporting ? "#FFFFFF" : "#0F5D5E",
                fontSize: 13,
                fontWeight: 700,
                textTransform: "none",
                minWidth: 130,
                border: exporting ? "1px solid rgba(255,255,255,0.3)" : "none",
                transition: "all 0.3s ease",
                "&:hover": { bgcolor: exporting ? "rgba(255,255,255,0.15)" : "#E8F5F0" },
                "&.Mui-disabled": { color: "#FFFFFF" }
              }}
            >
              {exporting ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <CircularProgress size={14} sx={{ color: "#FFFFFF" }} />
                  Generating...
                </Box>
              ) : (
                "Export PDF"
              )}
            </Button>
          </Box>
        )}

        <Box
          ref={ref}
          sx={{ width: pageWidth, margin: autoExport ? "0 auto" : "80px auto 48px", position: "relative" }}
        >
          {exporting && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                zIndex: 1400,
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "center",
                pt: "30vh",
                bgcolor: "rgba(245, 247, 250, 0.65)",
                backdropFilter: "blur(4px)"
              }}
            >
              <Box
                sx={{
                  bgcolor: "#FFFFFF",
                  borderRadius: "16px",
                  p: "32px 40px",
                  boxShadow: "0 16px 48px rgba(15, 93, 94, 0.15), 0 4px 12px rgba(0,0,0,0.06)",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "16px",
                  minWidth: 260
                }}
              >
                <CircularProgress size={32} thickness={4} sx={{ color: "#0F5D5E" }} />
                <Typography sx={{ fontSize: 15, fontWeight: 700, color: "#071f35" }}>Generating PDF</Typography>
                <Typography sx={{ fontSize: 12, color: "#6B7280" }}>
                  Capturing {pageCount} page{pageCount !== 1 ? "s" : ""}...
                </Typography>
                <LinearProgress
                  sx={{
                    width: "100%",
                    height: 4,
                    borderRadius: 2,
                    bgcolor: "#E5E7EB",
                    "& .MuiLinearProgress-bar": {
                      bgcolor: "#0F5D5E",
                      borderRadius: 2
                    }
                  }}
                />
              </Box>
            </Box>
          )}
          {groupedPages.map((pageCards, pageIndex) => {
            const page = resolvedPageMeta[pageIndex];
            const layout = page.layout;
            const isTitlePage = layout === "title";
            const isSectionBreak = layout === "section";
            const showHeader = layout === "header-content";
            const { title: pageTitle, subtitle: pageSubtitle } = getPageTitleValues(
              pages[page.originalPageIndex],
              reportMeta
            );

            return (
              <Box
                key={pageIndex}
                data-pdf-page={pageIndex}
                sx={{
                  width: pageWidth,
                  height: pageHeight,
                  bgcolor: "#FFFFFF",
                  boxShadow: autoExport ? "none" : "0 8px 28px rgba(15,23,42,0.12)",
                  overflow: "hidden",
                  position: "relative",
                  display: "flex",
                  flexDirection: "column",
                  mb: pageIndex < pageCount - 1 ? (autoExport ? 0 : "24px") : 0,
                  ...(autoExport && pageIndex < pageCount - 1 ? { pageBreakAfter: "always" } : {}),
                  // Shared background image on every non-title page (white scrim emulates opacity).
                  ...(!isTitlePage && reportMeta.background
                    ? {
                        backgroundImage: `linear-gradient(rgba(255,255,255,${1 - (reportMeta.backgroundOpacity ?? 100) / 100}), rgba(255,255,255,${1 - (reportMeta.backgroundOpacity ?? 100) / 100})), url(${reportMeta.background})`,
                        // Stretch both the scrim and the image to fill the whole page (no zoom/crop, no gaps).
                        backgroundSize: "100% 100%",
                        backgroundPosition: "center",
                        backgroundRepeat: "no-repeat"
                      }
                    : {})
                }}
              >
                {isTitlePage && (
                  <TitlePageContent
                    readOnly
                    meta={reportMeta}
                    title={pageTitle}
                    subtitle={pageSubtitle}
                    pageHeight={pageHeight}
                  />
                )}

                {isSectionBreak && <SectionBreakHeader readOnly title={page.title ?? ""} onTitleChange={noop} />}

                {showHeader && (
                  <ReportHeader
                    readOnly
                    meta={reportMeta}
                    title={pageTitle}
                    subtitle={pageSubtitle}
                    period={period}
                    currency={currency}
                    filters={filters}
                  />
                )}

                {!isTitlePage && (
                  <Box
                    sx={{
                      // Match the editor's grid container padding (28px sides, 16px vertical) so card
                      // widths and heights are identical between canvas and preview.
                      p: "16px 28px",
                      flex: 1,
                      minHeight: 0,
                      overflow: "hidden"
                    }}
                  >
                    {(() => {
                      const renderCard = (card: CanvasCard) => {
                        const origId = originalCardIds[card.id] ?? card.id;
                        const expandValue = {
                          expanded: expandedRows[origId] ?? EMPTY_SET,
                          onToggle: (rowKey: string) => onToggleExpand(origId, rowKey),
                          indexOffsets: cardOffsets[card.id]
                        };
                        return (
                          <TableExpandProvider value={expandValue}>
                            <PreviewCardWrapper card={card} orientation={orientation} />
                          </TableExpandProvider>
                        );
                      };

                      // A page whose only content is a full-width table renders the table directly
                      // at its natural height, filling the whole content area. This bypasses the
                      // 60px grid-row quantization that otherwise reserves up to a full empty grid
                      // row below the table on every page — the exact "extra empty row" problem.
                      // Greedy row packing fills the area down to the page bottom.
                      const isFullTablePage =
                        pageCards.length === 1 &&
                        pageCards[0].config.type === "table" &&
                        pageCards[0].gridW >= GRID_CONSTANTS.GRID_COLUMNS;

                      if (isFullTablePage) {
                        // Hug the table's content rather than stretching to full height: on a
                        // full page the rows already fill it, and on the last page the card ends
                        // right after the final row instead of leaving a tall empty box.
                        return <Box>{renderCard(pageCards[0])}</Box>;
                      }

                      return (
                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: `repeat(${GRID_CONSTANTS.GRID_COLUMNS}, 1fr)`,
                            gridAutoRows: `${GRID_CONSTANTS.GRID_CELL_HEIGHT}px`,
                            gap: `${GRID_CONSTANTS.GAP}px`
                          }}
                        >
                          {pageCards.map((card) => (
                            <Box
                              key={card.id}
                              sx={{
                                gridColumn: `${card.gridX + 1} / span ${card.gridW}`,
                                gridRow: `${card.gridY + 1} / span ${card.gridH}`,
                                // Mixed pages keep the grid; let a table hug its content so its
                                // border stays flush with the last row instead of floating below.
                                ...(card.config.type === "table" ? { alignSelf: "start" } : {})
                              }}
                            >
                              {renderCard(card)}
                            </Box>
                          ))}
                        </Box>
                      );
                    })()}
                  </Box>
                )}

                {/* Footer — matches the editor (absolute, page number only). */}
                <Box
                  sx={{
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    borderTop: "1px solid #dbe8f5",
                    p: "12px 28px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end"
                  }}
                >
                  <Typography sx={{ fontSize: 10, color: "#92a7bc" }}>
                    Page {pageIndex + 1} of {pageCount}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    );
  }
);

PreviewMode.displayName = "PreviewMode";

interface PreviewCardWrapperProps {
  card: CanvasCard;
  orientation: ReportOrientation;
}

const PreviewCardWrapper: React.FC<PreviewCardWrapperProps> = ({ card, orientation }) => {
  const cardSizeCtx = useMemo(
    () => ({
      widthPercent: card.widthPercent,
      heightPx: card.heightPx,
      orientation,
      isEditMode: false
    }),
    [card.widthPercent, card.heightPx, orientation]
  );

  const hasTimeRange = "cardFilters" in card.config && !!card.config.cardFilters?.dateRange;

  return (
    <CardSizeProvider value={cardSizeCtx}>
      <Box
        sx={{
          width: "100%",
          height: "100%",
          minWidth: 0,
          bgcolor: "#FFFFFF",
          borderRadius: "8px",
          boxShadow: "0 1px 4px rgba(0,0,0,0.07), 0 2px 12px rgba(0,0,0,0.04)",
          border: "1px solid #F3F4F6",
          overflow: "hidden",
          position: "relative",
          display: "flex",
          flexDirection: "column"
        }}
      >
        {hasTimeRange && (
          <Box sx={{ flexShrink: 0, display: "flex", justifyContent: "flex-end", px: "8px", pt: "6px" }}>
            <CardTimeRangeChip config={card.config} isPreview />
          </Box>
        )}
        <Box sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
        {card.config.type === "kpi" && <KpiCard data={card.data as KPICardData | undefined} loading={card.loading} />}
        {card.config.type === "summary" && (
          <SummaryCard data={card.data as SummaryCardData | undefined} loading={card.loading} />
        )}
        {card.config.type === "chart" && (
          <ChartCard data={card.data as Chart | undefined} loading={card.loading} animation={false} />
        )}
        {card.config.type === "table" && (
          <DataTableCard
            data={card.data as DataTableCardData | undefined}
            loading={card.loading}
            columnSettings={(card.config as TableCardConfig).columns}
            columnWidths={(card.config as TableCardConfig).columnWidths}
          />
        )}
        {card.config.type === "text" && <TextCard config={card.config} isPreview />}
        {card.config.type === "ai_card" && <AICard config={card.config} data={card.data} isPreview />}
        {card.config.type === "exposure" && (
          <ExposureBreakdownCard
            config={card.config}
            data={card.data as ExposureCardData | undefined}
            loading={card.loading}
          />
        )}
        {card.config.type === "cashflow" && (
          <CashFlowTimelineCard data={card.data as CashFlowTimelineCardData[] | undefined} />
        )}
        {card.config.type === "unfunded" && (
          <UnfundedCommitmentsCard data={card.data as UnfundedCardData[] | undefined} />
        )}
        {card.config.type === "performance" && (
          <PerformanceAttributionCard
            config={card.config}
            data={card.data as PerformanceAttributionCardData | undefined}
            loading={card.loading}
          />
        )}
        {card.config.type === "benchmark" && (
          <BenchmarkComparisonCard
            config={card.config}
            data={card.data as BenchmarkComparisonCardData | undefined}
            loading={card.loading}
          />
        )}
        {card.config.type === "dataFreshness" && (
          <DataFreshnessCard data={card.data as DataFreshnessCardData | undefined} />
        )}
        {card.config.type === "macroBrief" && (
          <MacroBriefCard
            data={card.data as MacroBriefCardData | undefined}
            config={card.config as MacroBriefCardConfig}
            loading={card.loading}
          />
        )}
        </Box>
      </Box>
    </CardSizeProvider>
  );
};

export { PreviewMode };
